import { Image, Marker, MarkerType } from '@eagle/api-types';
import { PointOfInterestSetUpdatableProps } from '@eagle/core-data-types';
// eslint-disable-next-line no-restricted-imports
import { Briefcase, Camera, FaceSmile, Fuel, Headphones, Heart, Install, Server, Star, Truck, Water, WeatherSun, Zap } from '@ethings/icons';
import { Box, Collapse, Divider, FormControlLabel, FormGroup, Radio, RadioGroup, Stack, styled, Switch, Typography } from '@ethings/ui-toolkit';
import { useTheme } from '@mui/material';
import { ComponentProps, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { ColorPicker, DEFAULT_COLORS, useMarkerContext } from '../../components';
import { IconPicker, PickerIcon } from '../../components/icon-picker';
import { extractImageBase64String, MediaUpload } from '../../components/upload/media-upload';
import { useResponsiveText } from '../../hooks';
import { Maybe, Nullable, SetState, Undefinable } from '../../types';
import { innerText } from '../../util';

type PointOfInterest = Omit<PointOfInterestSetUpdatableProps, 'accountBinding' | 'shared'>

interface Props<T extends PointOfInterest> {
  displayTopDivider?: boolean;
  hasCustomMarker?: boolean;
  readonly?: boolean;
  setPoiState: SetState<T>;
}

const StyledFormGroup = styled(FormGroup)({
  flexDirection: 'row',
  justifyContent: 'space-between',
});

const StyledFormControlLabel = styled(FormControlLabel)({
  justifyContent: 'space-between',
  width: '100%',
  margin: 0,
});

const StyledRadioGroup = styled(RadioGroup)({
  flexDirection: 'row',
  flexWrap: 'nowrap',
});

const MarkerTypeStack = styled(Stack)({
  '.MuiBox-root': {
    width: '100%',
  },
});

export interface CustomMarkerIcon {
  name: string;
  icon: JSX.Element;
}

export const CUSTOM_MARKER_ICONS: Readonly<CustomMarkerIcon[]> = [
  { name: 'Car Rental', icon: <Truck /> },
  { name: 'Electric Bike', icon: <Zap /> },
  { name: 'Fuel Station', icon: <Fuel /> },
  { name: 'Electrical', icon: <Install /> },
  { name: 'Weather', icon: <WeatherSun /> },
  { name: 'Meditate', icon: <Headphones /> },
  { name: 'Storage', icon: <Server /> },
  { name: 'Airport Shuttle', icon: <Briefcase /> },
  { name: 'Electric Scooter', icon: <Truck /> },
  { name: 'Star', icon: <Star /> },
  { name: 'Medical', icon: <Heart /> },
  { name: 'Painting', icon: <Camera /> },
  { name: 'Shower', icon: <Water /> },
  { name: 'Smiley', icon: <FaceSmile /> },
] as const;

/**
  * NATIVE FUNCTION: Point of Interest custom marker tool
  */
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
export function PointOfInterestCustomMarker<T extends PointOfInterest>({ setPoiState, displayTopDivider = true, hasCustomMarker = false, readonly }: Props<T>): JSX.Element {
  const [showOptions, setShowOptions] = useState<boolean>(hasCustomMarker);
  const { t } = useTranslation(['common']);
  const theme = useTheme();
  const {
    color,
    icon,
    iconType,
    resetStyles,
    setColor,
    setIcon,
    setIconType,
    setImage,
    setPreviewImage,
  } = useMarkerContext();

  const customMarkerText = t('common:component.custom-marker.custom-marker.labels');
  const h6Styles = useResponsiveText('1.25rem', innerText(customMarkerText).length, {
    minFontSize: '1rem',
    maxFontSize: '1.25rem',
    textLengthThreshold: 20,
  });

  const onChange = (rawImage: Undefinable<File>, previewImage: Maybe<string | ArrayBuffer>): void => {
    const newImage: Nullable<Image> = rawImage ? { filename: rawImage.name, contentType: rawImage.type } : null;
    const newImageData = extractImageBase64String(previewImage);
    setPoiState((prev) => ({ ...prev, image: newImage, imageData: newImageData }));
    setImage(newImage);
    setPreviewImage(previewImage);
  };

  const dotTypeChange = (_event: unknown, value: string): void => {
    const type = value as MarkerType;
    setPoiState((prev) => {
      const marker: Marker = { type, color: prev.marker?.color ?? color };
      return ({ ...prev, marker });
    });
  };

  const colorChange = (updatedColor: string): void => {
    setPoiState((prev) => {
      const marker = { color: updatedColor, type: prev.marker?.type ?? iconType };
      return ({ ...prev, marker });
    });
    setColor(updatedColor);
  };

  const iconChange = (updatedIcon: Undefinable<PickerIcon>): void => {
    setPoiState((prev) => ({ ...prev, icon: updatedIcon?.name ?? null }));
    setIcon(updatedIcon);
  };

  const handleShowOptionsChange: ComponentProps<'input'>['onChange'] = (e) => {
    if (e.target.checked) {
      setShowOptions(true);
    }
    else {
      setShowOptions(false);
      setPoiState((prev) => ({ ...prev, marker: null, icon: null, image: null }));
      resetStyles();
    }
  };

  return (
    <Stack>
      {displayTopDivider && <Box mb={2}><Divider /></Box>}
      <StyledFormGroup data-testid="custom-marker">
        <StyledFormControlLabel
          disabled={readonly}
          control={<Switch checked={showOptions} size="small" onChange={handleShowOptionsChange} data-testid="custom-marker-switch" />}
          label={
            <Typography
              variant="h6"
              style={{
                fontSize: h6Styles.fontSize,
                lineHeight: h6Styles.lineHeight,
                wordBreak: h6Styles.wordBreak,
                whiteSpace: h6Styles.whiteSpace,
                overflow: h6Styles.overflow,
                textOverflow: h6Styles.textOverflow,
              }}
            >
              {customMarkerText}
            </Typography>
          }
          labelPlacement="start"
        />
      </StyledFormGroup>
      <Collapse in={showOptions}>
        <Stack spacing={3} mt={2}>
          <MarkerTypeStack flexDirection="row">
            <StyledRadioGroup
              data-testid="marker-type"
              name="radio-buttons-group"
              onChange={dotTypeChange}
              value={iconType}
            >
              <FormControlLabel
                data-testid={`${MarkerType.PIN}-input`}
                disabled={readonly}
                componentsProps={{ typography: { fontSize: '12px' } }}
                control={<Radio checked={iconType === MarkerType.PIN} onClick={() => setIconType(MarkerType.PIN)} size="medium" />}
                label={t('common:component.custom-marker.pin.labels')}
                value={MarkerType.PIN}
              />
              <FormControlLabel
                data-testid={`${MarkerType.DOT}-input`}
                disabled={readonly}
                componentsProps={{ typography: { fontSize: '12px' } }}
                control={<Radio checked={iconType === MarkerType.DOT} onClick={() => setIconType(MarkerType.DOT)} size="medium" />}
                label={t('common:component.custom-marker.dot.labels')}
                value={MarkerType.DOT}
              />
            </StyledRadioGroup>
            <ColorPicker
              data-testid="color-picker"
              disabled={readonly}
              color={color}
              colors={[theme.palette.primary.main, ...DEFAULT_COLORS]}
              onChange={colorChange}
            />
          </MarkerTypeStack>
          <IconPicker
            data-testid="icon-picker"
            disabled={readonly}
            icons={CUSTOM_MARKER_ICONS}
            mode="inline"
            onChange={iconChange}
            placeholder={t('component.custom-marker.select-icon.labels')}
            value={icon}
          />
          {!readonly && (
            <>
              <Typography textAlign='center'>{t('common:component.filter.labels.or')}</Typography>
              <MediaUpload disablePreview onChange={onChange} />
            </>
          )}
        </Stack>
      </Collapse>
    </Stack>
  );
}
